import { RewardPoints, SuccessWithShadow, useSettings, WarningWithShadow } from '@capsicum/common';
import { BaseTextInput, Button, Image, ModalService, Text, useRouter, useWindowSize } from '@capsicum/common-web';
import { VoucherIcon } from '@capsicum/common-web/icons';
import { useRedeemVoucher } from '@capsicum/features/wallet/shared';
import { type ChangeEvent, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

export const Voucher = () => {
	const { breakpoints } = useTheme();
	const { dimensions } = useWindowSize();
	const router = useRouter();
	const { settings } = useSettings();
	const { errorMessage, mutateRedeemVoucher, setVoucherCode, texts, voucherCode, voucherData, voucherError } =
		useRedeemVoucher();
	const isMobileView = useMemo(
		() => dimensions.width < Number(breakpoints.points.mobile.maxScreenWidth),
		[dimensions, breakpoints]
	);

	useEffect(() => {
		if (!settings?.isVoucherEnabled) {
			router.replace('/wallet/fan-tokens');
		}
	}, [settings?.isVoucherEnabled, router]);

	useEffect(() => {
		if (voucherData?.data.referenceNumber?.length) {
			ModalService.addModal({
				detached: isMobileView,
				renderComponent: () => (
					<S.VoucherSuccessWrapper>
						<S.StyledImage alt="success-icon" height={121} src={SuccessWithShadow} width={150} />
						<S.SuccessTitle variant="title6">{texts.succesfullyRedeemed}</S.SuccessTitle>
						<S.SuccessMessage variant="text3">{texts.congratsRedeem}</S.SuccessMessage>
					</S.VoucherSuccessWrapper>
				),
				renderFooter: ({ close }) => (
					<S.ButtonWrapper>
						<Button
							color="brand"
							fullWidth={isMobileView}
							testID="voucher-to-transaction-history-button"
							text={texts.checkWallet}
							onClick={() => {
								router.push('/wallet');
								close();
							}}
						/>
					</S.ButtonWrapper>
				)
			});
		}

		if (voucherError?.response?.data?.errors?.length) {
			ModalService.addModal({
				detached: isMobileView,
				renderComponent: () => (
					<S.VoucherSuccessWrapper>
						<S.StyledImage alt="warning-icon" height={121} src={WarningWithShadow} width={150} />
						<S.ErrorTitle variant="title6">{errorMessage}</S.ErrorTitle>
					</S.VoucherSuccessWrapper>
				),
				renderFooter: ({ close }) => (
					<S.ButtonWrapper>
						<Button
							color="brand"
							fullWidth={isMobileView}
							testID="voucher-to-transaction-history-button"
							text={texts.gotIt}
							onClick={() => close()}
						/>
					</S.ButtonWrapper>
				)
			});
		}
	}, [errorMessage, voucherData, voucherError]);

	return (
		<S.Wrapper>
			<S.VoucherWrapper>
				<S.TitleWrapper>
					<Image alt="reward points logo" height={25} src={RewardPoints} width={32} />

					<S.Title variant="title3">{texts.sociosTitle}</S.Title>
				</S.TitleWrapper>
				<S.FieldWrapper>
					<S.StyledBaseTextInput
						label={texts.placeholder}
						testID="voucher-input-field"
						value={voucherCode}
						onChange={(event: ChangeEvent<HTMLInputElement>) =>
							setVoucherCode(event.target.value.toUpperCase())
						}
					/>
					<S.ButtonWrapper>
						<Button
							color="brand"
							disabled={voucherCode.length <= 7}
							fullWidth
							testID="voucher-button-redeem"
							text={texts.redeemVoucher}
							onClick={() => mutateRedeemVoucher({ voucherCode })}
						/>
					</S.ButtonWrapper>
				</S.FieldWrapper>
			</S.VoucherWrapper>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	`;

	export const VoucherWrapper = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		margin: ${({ theme }) => `${theme.spacings.large} ${theme.spacings.medium} 0`};
		padding: ${({ theme }) => `${theme.spacings.large} ${theme.spacings.small}`};
		${({ theme }) => theme.border.radius.medium};
	`;

	export const TitleWrapper = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Title = styled(Text)`
		${({ theme }) => theme.fonts.headings.title3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledBaseTextInput = styled(BaseTextInput)`
		margin: ${({ theme }) => theme.spacings.xlarge} 0;
	`;

	export const StyledVoucherIcon = styled(VoucherIcon)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		align-self: center;
	`;

	export const FieldWrapper = styled.div`
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			width: 50%;
		}
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		justify-content: center;
	`;

	export const VoucherSuccessWrapper = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	`;

	export const StyledImage = styled(Image)`
		margin-bottom: ${({ theme }) => theme.spacings.medium};
	`;

	export const SuccessTitle = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const ErrorTitle = styled(Text)`
		text-align: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		width: 80%;
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			width: 90%;
		}
	`;

	export const SuccessMessage = styled(Text)`
		text-align: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		width: 80%;
	`;
}
